"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { lmsService, LMSCourse } from "@/services/lms"
import { ExternalLink, GraduationCap, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"

// ─── Success Stories types ──────────────────────────────────────────────────
interface SuccessStory {
    id: number
    university: string
    filename: string
    imageUrl: string
}

const SS_CACHE_KEY = "success_stories_cache"
const SS_CACHE_TIME_KEY = "success_stories_cache_time"
const SS_CACHE_DURATION = 60 * 60 * 1000

// The API's imageUrl is a "/assets/premia_education_success/…" path that only exists
// on the marketing site, so it 404s here. The same images ship locally in
// /public/premia_success (filenames match the API's `filename`); use those.
const localPremiaSrc = (filename?: string) =>
    filename ? `/premia_success/${encodeURIComponent(filename)}` : ""

const swapToRemoteOnError = (remoteUrl?: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (img.dataset.fallback !== "1" && remoteUrl) {
        img.dataset.fallback = "1"
        img.src = remoteUrl
    }
}

const LMS_CACHE_KEY = "lms_courses_cache"
const LMS_CACHE_TIME_KEY = "lms_courses_cache_time"
const LMS_CACHE_DURATION = 30 * 60 * 1000

type Phase = "courses" | "stories"

export function FeaturedCarousel() {
    // ─── Data ───────────────────────────────────────────────────────────────
    const [courses, setCourses] = useState<LMSCourse[]>([])
    const [stories, setStories] = useState<SuccessStory[]>([])
    const [loading, setLoading] = useState(true)

    // ─── Phase / transition ─────────────────────────────────────────────────
    const [phase, setPhase] = useState<Phase>("courses")
    const [fading, setFading] = useState(false)

    // ─── Scroll refs ────────────────────────────────────────────────────────
    const scrollRef = useRef<HTMLDivElement>(null)
    const [isHovered, setIsHovered] = useState(false)

    // ─── Lightbox (only for success stories) ────────────────────────────────
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
    const openLightbox = (i: number) => setLightboxIndex(i)
    const closeLightbox = () => setLightboxIndex(null)
    const goPrev = useCallback(() => {
        setLightboxIndex((i) => (i !== null ? (i - 1 + stories.length) % stories.length : null))
    }, [stories.length])
    const goNext = useCallback(() => {
        setLightboxIndex((i) => (i !== null ? (i + 1) % stories.length : null))
    }, [stories.length])

    useEffect(() => {
        if (lightboxIndex === null) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeLightbox()
            if (e.key === "ArrowLeft") goPrev()
            if (e.key === "ArrowRight") goNext()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [lightboxIndex, goPrev, goNext])

    // ─── Fetch both data sources ────────────────────────────────────────────
    useEffect(() => {
        let mounted = true
        const load = async () => {
            const now = Date.now()

            // LMS courses
            let coursesData: LMSCourse[] = []
            const lmsCache = localStorage.getItem(LMS_CACHE_KEY)
            const lmsTime = localStorage.getItem(LMS_CACHE_TIME_KEY)
            if (lmsCache && lmsTime && now - parseInt(lmsTime) < LMS_CACHE_DURATION) {
                coursesData = JSON.parse(lmsCache)
            } else {
                try {
                    const res = await lmsService.getOpenEnrollment()
                    if (res.success) {
                        coursesData = res.courses
                        localStorage.setItem(LMS_CACHE_KEY, JSON.stringify(coursesData))
                        localStorage.setItem(LMS_CACHE_TIME_KEY, now.toString())
                    }
                } catch { /* silent */ }
            }

            // Success stories
            let storiesData: SuccessStory[] = []
            const ssCache = localStorage.getItem(SS_CACHE_KEY)
            const ssTime = localStorage.getItem(SS_CACHE_TIME_KEY)
            if (ssCache && ssTime && now - parseInt(ssTime) < SS_CACHE_DURATION) {
                storiesData = JSON.parse(ssCache)
            } else {
                try {
                    const res = await fetch("https://dsatschool.com/api/public/premia-education-success")
                    if (res.ok) {
                        const data = await res.json()
                        storiesData = data.items
                        localStorage.setItem(SS_CACHE_KEY, JSON.stringify(storiesData))
                        localStorage.setItem(SS_CACHE_TIME_KEY, now.toString())
                    }
                } catch { /* silent */ }
            }

            if (mounted) {
                setCourses(coursesData)
                setStories(storiesData)
                setPhase(coursesData.length > 0 ? "courses" : "stories")
                setLoading(false)
            }
        }
        load()
        return () => { mounted = false }
    }, [])

    // ─── Auto-scroll + phase switching ──────────────────────────────────────
    useEffect(() => {
        if (loading || isHovered) return
        const hasC = courses.length > 0
        const hasS = stories.length > 0
        if (!hasC && !hasS) return

        const container = scrollRef.current
        if (!container) return

        const scrollAmount = phase === "courses" ? 256 : 236

        const interval = setInterval(() => {
            const { scrollLeft, scrollWidth, clientWidth } = container

            // Reached the end of scroll
            if (scrollLeft + clientWidth >= scrollWidth - 10) {
                // Switch phase if both collections exist
                if (hasC && hasS) {
                    const nextPhase: Phase = phase === "courses" ? "stories" : "courses"
                    setFading(true)
                    setTimeout(() => {
                        setPhase(nextPhase)
                        // After phase switch, scroll back to start
                        setTimeout(() => {
                            scrollRef.current?.scrollTo({ left: 0 })
                            setFading(false)
                        }, 50)
                    }, 300)
                } else {
                    // Only one collection — loop it
                    container.scrollTo({ left: 0, behavior: "smooth" })
                }
            } else {
                container.scrollBy({ left: scrollAmount, behavior: "smooth" })
            }
        }, 3000)

        return () => clearInterval(interval)
    }, [loading, isHovered, phase, courses.length, stories.length])

    // ─── Derived ────────────────────────────────────────────────────────────
    const isCourses = phase === "courses"
    const title = isCourses ? "Featured Courses" : "Student Success Stories"
    const subtitle = isCourses ? "From DSAT School" : "Premia Education"

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8 bg-card rounded-3xl border border-border">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        )
    }

    if (courses.length === 0 && stories.length === 0) return null

    return (
        <>
            <div className="space-y-3">
                {/* Header — animates label swap */}
                <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                        {!isCourses && <GraduationCap className="h-4 w-4 text-muted-foreground" />}
                        <h3 className="font-bold text-base transition-opacity duration-300">{title}</h3>
                    </div>
                    <span className="text-sm text-muted-foreground transition-opacity duration-300">{subtitle}</span>
                </div>

                {/* Phase indicator dots */}
                {courses.length > 0 && stories.length > 0 && (
                    <div className="flex items-center justify-center gap-1.5 px-1">
                        <button
                            onClick={() => { setPhase("courses"); scrollRef.current?.scrollTo({ left: 0 }) }}
                            className={`h-1.5 rounded-full transition-all duration-300 ${isCourses ? "w-4 bg-foreground" : "w-1.5 bg-muted-foreground/40"}`}
                            aria-label="Show courses"
                        />
                        <button
                            onClick={() => { setPhase("stories"); scrollRef.current?.scrollTo({ left: 0 }) }}
                            className={`h-1.5 rounded-full transition-all duration-300 ${!isCourses ? "w-4 bg-foreground" : "w-1.5 bg-muted-foreground/40"}`}
                            aria-label="Show success stories"
                        />
                    </div>
                )}

                {/* Carousel container */}
                <div
                    ref={scrollRef}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onTouchStart={() => setIsHovered(true)}
                    onTouchEnd={() => setIsHovered(false)}
                    className={`flex overflow-x-auto pb-4 gap-4 snap-x snap-mandatory scrollbar-hide -mx-1 px-1 transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                    {isCourses
                        ? courses.map((course) => (
                            <a
                                key={course.id}
                                href={course.course_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative min-w-[240px] max-w-[240px] aspect-[3/4] flex-shrink-0 snap-center rounded-3xl overflow-hidden border border-border bg-muted shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group"
                            >
                                {course.thumbnail ? (
                                    <Image
                                        src={course.thumbnail}
                                        alt={course.title}
                                        fill
                                        unoptimized
                                        loading="eager"
                                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                                        No Image
                                    </div>
                                )}
                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-2 rounded-full text-white backdrop-blur-sm">
                                    <ExternalLink className="w-4 h-4" />
                                </div>
                            </a>
                        ))
                        : stories.map((story, index) => (
                            <div
                                key={story.id}
                                onClick={() => openLightbox(index)}
                                className="relative min-w-[240px] max-w-[240px] aspect-[3/4] flex-shrink-0 snap-center rounded-3xl overflow-hidden border border-border bg-muted shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group cursor-zoom-in"
                            >
                                <Image
                                    src={localPremiaSrc(story.filename) || story.imageUrl}
                                    alt={`Accepted to ${story.university}`}
                                    fill
                                    unoptimized
                                    loading="eager"
                                    onError={swapToRemoteOnError(story.imageUrl)}
                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pt-10 pb-3 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                                    <p className="text-white text-xs font-semibold leading-tight line-clamp-2 drop-shadow">
                                        {story.university}
                                    </p>
                                    <p className="text-white/60 text-[10px] mt-0.5">Accepted ✓</p>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>

            {/* Lightbox for success stories */}
            {lightboxIndex !== null && (
                <div
                    className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4"
                    onClick={closeLightbox}
                >
                    <div className="flex items-center justify-between w-full max-w-lg px-4" onClick={(e) => e.stopPropagation()}>
                        <span className="text-muted-foreground text-sm tabular-nums">
                            {lightboxIndex + 1} / {stories.length}
                        </span>
                        <button
                            onClick={closeLightbox}
                            className="p-1.5 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors border border-border shadow-sm"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 px-4 w-full justify-center" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={goPrev}
                            className="flex-shrink-0 p-2 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors border border-border shadow-sm"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="relative rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl border border-border">
                            <Image
                                src={localPremiaSrc(stories[lightboxIndex].filename) || stories[lightboxIndex].imageUrl}
                                alt={`Accepted to ${stories[lightboxIndex].university}`}
                                width={480}
                                height={640}
                                unoptimized
                                onError={swapToRemoteOnError(stories[lightboxIndex].imageUrl)}
                                className="object-contain w-full h-auto max-h-[70vh]"
                                style={{ display: "block" }}
                            />
                        </div>

                        <button
                            onClick={goNext}
                            className="flex-shrink-0 p-2 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors border border-border shadow-sm"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    <p className="text-foreground text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        {stories[lightboxIndex].university}
                    </p>
                </div>
            )}
        </>
    )
}
